import axios from '../../../app/api-client.ts';
import { FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { UserModel } from '../../../types';
import {
  createResponseContext,
  initialQueryResponse,
  initialQueryState,
  PaginationState,
  QueryResponseContextProps,
  Response,
  stringifyRequestQuery,
} from '../../helpers';
import { useQueryRequest } from './QueryRequestProvider';
import {getEnvironment} from '../../../app/environment';
import {selectApplication} from '../../../app/store/application';
import {useAppSelector} from '../../../app/store/hooks';

async function fetchData<T>(
  url: string,
  query: string,
  signal?: AbortSignal
): Promise<Response<T[]>> {
  const d = await axios.get(
    `${url}${url.indexOf('?') >= 0 ? '&' : '?'}${query}`, {signal}
  );
  return d.data;
}

const QueryResponseContext =
  createResponseContext<unknown>(initialQueryResponse);
const QueryResponseProvider: FC<{
  children?: ReactNode;
  url: string;
  queryList?: string;
}> = ({ children, url, queryList }) => {
  const { state } = useQueryRequest();
  const application = useAppSelector(selectApplication);
  const [query, setQuery] = useState<string>(stringifyRequestQuery(state));
  const updatedQuery = useMemo(() => stringifyRequestQuery(state), [state]);

  useEffect(() => {
    if (query !== updatedQuery) {
      setQuery(updatedQuery);
    }
  }, [updatedQuery]);

  const {
    isFetching,
    isError,
    refetch,
    data: response,
  } = useQuery(
    [`${queryList ?? ''}-${query}`, url, getEnvironment(), application?.key ?? ''],
    ({signal}) => {
      return fetchData(url, query, signal);
    },
    { cacheTime: 0, keepPreviousData: false, refetchOnWindowFocus: false, retry: false }
  );

  return (
    <QueryResponseContext.Provider
      value={{ isLoading: isFetching, isError, refetch, response, query }}
    >
      {children}
    </QueryResponseContext.Provider>
  );
};

function useQueryResponse<T>() {
  return useContext(QueryResponseContext) as QueryResponseContextProps<T>;
}

function useQueryResponseData<T>() {
  const { response } = useQueryResponse<T>();
  if (!response) {
    return [];
  }

  return response?.data || [];
}

const useQueryResponsePagination = () => {
  const defaultPaginationState: PaginationState = {
    links: [],
    ...initialQueryState,
  };

  const { response } = useQueryResponse();
  if (!response || !response.payload || !response.payload.pagination) {
    return defaultPaginationState;
  }

  return response.payload.pagination;
};

const useQueryResponseLoading = (): boolean => {
  const { isLoading } = useQueryResponse();
  return isLoading;
};

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponseLoading,
  useQueryResponsePagination,
};
